import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { parseCron } from '../types/program';
import type { FlexPricingRow } from '../utils/flexPlan';

/**
 * A flex program's pricing rows, in the shape `splitFlexPricing` expects.
 *
 * This exists because four components each fetched and mapped these rows themselves, and the
 * copies drifted: `EnrollmentDetailPanel` asked the query for `is_optional` but its mapping
 * dropped it, so every row looked non-optional and its per-day rate silently included add-ons
 * the family had not chosen. Nothing failed — the estimate was just wrong — and it survived
 * two passes over this code because the other three copies were right.
 *
 * Callers still decide what to do with the rows: which selection to price against, whether to
 * include one-time materials. Only the fetching and the row shape live here, because that is
 * the part that has to be identical and was not.
 */

type RawPricingRow = {
  amount: number;
  is_optional?: boolean;
  line_item_configs?:
    | { id?: string | null; type?: string | null; subtype?: string | null }
    | Array<{ id?: string | null; type?: string | null; subtype?: string | null }>
    | null;
};

export type FlexPricing = {
  rows: FlexPricingRow[];
  /** Weekdays the program meets, 0-6, from its cron. */
  weekdays: number[];
};

export function useFlexPricingRows(
  programId: string | null | undefined,
  scheduleCron?: string | null,
  opts: { enabled?: boolean } = {},
) {
  return useQuery<FlexPricing>({
    // Deliberately keyed on the program alone: the rows do not depend on any enrollment's
    // selection, so every panel showing the same program shares one fetch.
    queryKey: ['flex-pricing-rows', programId, scheduleCron],
    enabled: (opts.enabled ?? true) && !!programId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pricing')
        .select('amount, is_optional, line_item_configs:line_item_config_id ( id, type, subtype )')
        .eq('program_id', programId as string);
      if (error) throw error;

      const rows: FlexPricingRow[] = ((data ?? []) as RawPricingRow[]).map((row) => {
        const cfg = Array.isArray(row.line_item_configs)
          ? row.line_item_configs[0]
          : row.line_item_configs;
        return {
          id: cfg?.id ?? undefined,
          amount: row.amount,
          type: cfg?.type ?? undefined,
          subtype: cfg?.subtype ?? undefined,
          // The field the duplicated copies disagreed about. An optional row is charged only
          // when the family's opt_in_items names it, so losing this flag silently prices
          // every add-on into the quote.
          isOptional: row.is_optional === true,
        };
      });

      return { rows, weekdays: (parseCron(scheduleCron || '').days || []).map((d) => d % 7) };
    },
  });
}
